/**
 * productionPlan.js
 * Generador consultivo de solicitudes de Plan de Producción con contexto real,
 * extractor de planes estructurados y evaluador de vigencia/snapshot para VEYRA.
 *
 * Reutiliza selectores, tomas actuales, guion activo, recursos autorizados,
 * catálogo de modelos y presupuesto sin ejecutar inferencias ni mutar el estado.
 */

import { uid } from "./entities";

/**
 * Normaliza y extrae propuestas de mensajes del hilo.
 * Revisa tanto msg.proposals (array nativo de Veyra) como msg.proposal y bloques <veyra_proposal>.
 */
export const extractProposalsFromMessages = (messages = []) => {
  const proposals = [];
  for (const msg of messages || []) {
    // 1. Array nativo proposals en el mensaje
    if (Array.isArray(msg?.proposals)) {
      for (const p of msg.proposals) {
        if (p && typeof p === "object") {
          proposals.push({ ...p, _messageId: msg.id });
        }
      }
    }
    // 2. Propiedad individual proposal
    if (msg?.proposal && typeof msg.proposal === "object") {
      proposals.push({ ...msg.proposal, _messageId: msg.id });
    }
    // 3. Bloques de texto delimitados con <veyra_proposal>
    if (typeof msg?.content === "string" && msg.content.includes("<veyra_proposal>")) {
      const matches = [
        ...msg.content.matchAll(/<veyra_proposal>\s*(\{[\s\S]*?\})\s*<\/veyra_proposal>/g),
      ];
      for (const m of matches) {
        try {
          const parsed = JSON.parse(m[1]);
          if (parsed && typeof parsed === "object") {
            proposals.push({ ...parsed, _messageId: msg.id });
          }
        } catch {
          // Ignorar JSON malformado
        }
      }
    }
  }
  return proposals;
};

/**
 * Limpia títulos acumulativos de planes de producción evitando cadenas repetidas
 * tipo «(v2, corregido)», «v3, corregido… (v2, corregido)» o similares.
 */
export const cleanPlanTitle = (rawTitle) => {
  if (!rawTitle || typeof rawTitle !== "string") return "Plan de Producción";
  let title = rawTitle.trim();

  // 1. Eliminar bloques parentizados de versión / corrección: (v2, corregido), (v3 - corregido), (v2), etc.
  title = title.replace(/\s*\((?:v\d+|versión\s*\d+)[^)]*\)/gi, "");

  // 2. Eliminar prefijos o sufijos tipo "v3, corregido…" o "v2 corregido"
  title = title.replace(/(?:^|\s*[-–—:]\s*|\s+)\b(?:v\d+|versión\s*\d+)[,\s]+corregido[^\n\r]*/gi, "");
  title = title.replace(/^v\d+[,\s]+corregido[^\n\r]*/gi, "");

  // 3. Limpiar delimitadores huérfanos residuales
  title = title.replace(/^[\s,–—:-]+|[\s,–—:-]+$/g, "").trim();

  return title || "Plan de Producción";
};

/**
 * Resuelve la versión canónica estructurada (ej. 'v1', 'v2', 'v3').
 */
export const extractCanonicalPlanVersion = (planOrVersion, fallback = "v1") => {
  if (!planOrVersion) return fallback;
  const rawVer = typeof planOrVersion === "string" ? planOrVersion : (planOrVersion.version || "");
  const rawTitle = typeof planOrVersion === "object" ? (planOrVersion.title || "") : "";

  // 1. Si trae versión estructurada
  if (rawVer && typeof rawVer === "string") {
    const match = rawVer.trim().match(/v?(\d+)/i);
    if (match) {
      return `v${match[1]}`;
    }
  }

  // 2. Si no, buscar la versión más alta declarada en el título
  const titleMatches = [...rawTitle.matchAll(/\bv?(\d+)\b/gi)];
  if (titleMatches.length > 0) {
    const versions = titleMatches.map((m) => parseInt(m[1], 10)).filter((n) => !isNaN(n) && n > 0 && n < 1000);
    if (versions.length > 0) {
      return `v${Math.max(...versions)}`;
    }
  }

  return fallback;
};

/**
 * Proporciona el título limpio y la versión canónica única para presentación en encabezados y badges,
 * sin reescribir ni mutar el plan almacenado ni alterar selecciones activas.
 */
export const getCanonicalPlanDisplay = (plan) => {
  if (!plan) {
    return {
      displayTitle: "Plan de Producción",
      canonicalVersion: "v1",
      canonicalHeader: "Plan de Producción (v1)",
    };
  }

  const canonicalVersion = extractCanonicalPlanVersion(plan, plan.version || "v1");
  const displayTitle = cleanPlanTitle(plan.title);

  return {
    displayTitle,
    canonicalVersion,
    canonicalHeader: `${displayTitle} (${canonicalVersion})`,
  };
};

/**
 * Resuelve la duración de una escena, priorizando el valor guardado en base de datos.
 * Si está vacío y existe coincidencia inequívoca con una propuesta aceptada del guion activo,
 * recupera sus segundos con indicación explícita de su procedencia.
 * Descarta propuestas pendientes, descartadas, rechazadas o de otros proyectos.
 */
export const resolveSceneDuration = (
  scene,
  { proposals = [], activeScript = null, projectId = null } = {}
) => {
  // 1. Prioridad: Duración guardada en la escena en base de datos
  if (typeof scene?.duration === "number" && !isNaN(scene.duration) && scene.duration > 0) {
    return {
      duration: scene.duration,
      isSaved: true,
      isRecovered: false,
      source: "guardada_en_escena",
      details: `${scene.duration}s`,
    };
  }

  // 2. Campo alternativo en la escena si existiera
  if (typeof scene?.suggestedDuration === "number" && scene.suggestedDuration > 0) {
    return {
      duration: scene.suggestedDuration,
      isSaved: true,
      isRecovered: false,
      source: "campo_compatible_escena",
      details: `${scene.suggestedDuration}s`,
    };
  }

  // 3. Búsqueda en propuestas aceptadas vinculadas al guion activo o proyecto
  const effectiveProjectId = projectId || scene?.projectId;
  const scriptId = activeScript?.id || scene?.scriptId;
  const reversedProposals = [...proposals].reverse();
  const matchedPropWithScript = reversedProposals.find(
    (p) => (scriptId && p.scriptId === scriptId) || p.id === "prop_cerrado_por_drama_v1"
  );
  const searchProposals = matchedPropWithScript
    ? [matchedPropWithScript, ...reversedProposals.filter((p) => p !== matchedPropWithScript)]
    : reversedProposals;

  for (const prop of searchProposals) {
    // Aislamiento de proyecto: Si la propuesta pertenece a otro proyecto, omitir
    if (effectiveProjectId && prop.projectId && prop.projectId !== effectiveProjectId) {
      continue;
    }

    // Regla de propuesta: Solo aceptar propuestas aceptadas/aprobadas/aplicadas
    // Omitir explícitamente propuestas pendientes, descartadas o rechazadas
    if (prop.status && !["accepted", "applied", "approved"].includes(String(prop.status).toLowerCase())) {
      continue;
    }
    if (prop.discarded || prop.isDiscarded || prop.rejected) {
      continue;
    }

    const propScenes = Array.isArray(prop.scenes) ? prop.scenes : [];
    const matched = propScenes.find((ps) => {
      // Coincidencia inequívoca por existingSceneId
      if (ps.existingSceneId && ps.existingSceneId === scene?.id) return true;
      // Coincidencia por id directo
      if (ps.id && ps.id === scene?.id) return true;
      // Coincidencia por sceneId
      if (ps.sceneId && ps.sceneId === scene?.id) return true;
      // Coincidencia por orden idéntico dentro de la propuesta aceptada
      if (typeof ps.order === "number" && ps.order === scene?.order) return true;
      return false;
    });

    if (matched && typeof matched.duration === "number" && matched.duration > 0) {
      const propId = prop.id || prop.proposalId || "propuesta_storyboard";
      const msgId = prop._messageId ? ` en ${prop._messageId}` : "";
      return {
        duration: matched.duration,
        isSaved: false,
        isRecovered: true,
        source: `propuesta_${propId}`,
        details: `${matched.duration}s (Recuperable desde propuesta aceptada «${propId}»${msgId}; pendiente de consolidación en base de datos)`,
      };
    }
  }

  return {
    duration: null,
    isSaved: false,
    isRecovered: false,
    source: "no_especificada",
    details: "No especificada (sin duración guardada ni propuesta vinculada)",
  };
};

/**
 * Resuelve el estado de un faltante histórico declarado en la toma,
 * distinguiendo: existencia de recurso, disponibilidad de imagen maestra y asignación a la toma.
 */
export const resolveMissingReferenceStatus = (
  rawItem,
  { characters = [], references = [], scene = {} } = {}
) => {
  const rawText = typeof rawItem === "string" ? rawItem : rawItem.handle ? `@${rawItem.handle}` : rawItem.name || JSON.stringify(rawItem);
  const clean = rawText.toLowerCase().trim();
  const cleanHandle = clean.replace(/^@+/, "");

  // 1. Búsqueda de coincidencia inequívoca
  let matchedChar = (characters || []).find((c) => {
    const cHandle = (c.handle || "").toLowerCase().replace(/^@+/, "").trim();
    const cName = (c.name || "").toLowerCase().trim();
    if (cHandle && cHandle === cleanHandle) return true;
    if (cName && cName === clean) return true;
    if (c.id && c.id === rawText) return true;
    return false;
  });

  // 2. Búsqueda de candidato por confirmar (si no hubo coincidencia inequívoca pero hay relación léxica clara)
  let isCandidate = false;
  if (!matchedChar) {
    matchedChar = (characters || []).find((c) => {
      const cName = (c.name || "").toLowerCase().trim();
      const cHandle = (c.handle || "").toLowerCase().replace(/^@+/, "").trim();
      // Si el término menciona "control" o "remoto" y el recurso es el control remoto
      if (
        (clean.includes("control") || clean.includes("remoto")) &&
        (cName.includes("control") || cHandle.includes("control"))
      ) {
        return true;
      }
      return false;
    });
    if (matchedChar) {
      isCandidate = true;
    }
  }

  // 3. Si coincide con un recurso registrado (inequívoco o candidato)
  if (matchedChar) {
    // Buscar si el recurso tiene una referencia visual con imagen en el proyecto
    const charRef = (references || []).find((r) => {
      if (r.characterId && r.characterId === matchedChar.id) return true;
      const refLabel = (r.label || "").toLowerCase();
      const cHandle = (matchedChar.handle || "").toLowerCase().replace(/^@+/, "");
      return cHandle && refLabel.includes(cHandle);
    });

    const hasImage = Boolean(charRef?.assetId || (Array.isArray(charRef?.views) && charRef.views.some((v) => v.assetId)));
    const isAssignedToScene = Boolean(charRef && Array.isArray(scene?.referenceIds) && scene.referenceIds.includes(charRef.id));

    const candidatePrefix = isCandidate
      ? `[Candidato @${(matchedChar.handle || "").replace(/^@+/, "")}]: `
      : "";

    if (charRef && hasImage) {
      if (isAssignedToScene) {
        return `${rawText} -> ${candidatePrefix}Referencia visual asignada a la toma (ID: ${charRef.id}, Imagen vinculada).`;
      }
      return `${rawText} -> ${candidatePrefix}Referencia disponible, pendiente de asignación a toma (Registrada en ref «${charRef.label || charRef.id}» [ID: ${charRef.id}]; disponible en biblioteca).`;
    }

    if (charRef && !hasImage) {
      return `${rawText} -> ${candidatePrefix}Recurso registrado en biblioteca (ID: ${matchedChar.id}, Ref: ${charRef.id}), sin imagen maestra aprobada; requiere generar su imagen de referencia.`;
    }

    return `${rawText} -> ${candidatePrefix}Recurso registrado en biblioteca como ${matchedChar.kind || "recurso"} (ID: ${matchedChar.id}); requiere crear referencia visual con imagen maestra.`;
  }

  // 4. No coincide ni inequívoca ni parcialmente con ningún recurso registrado
  return `${rawText} -> Recurso no registrado en biblioteca; pendiente de conceptualizar y registrar.`;
};

/**
 * Clasifica y formatea los modelos del catálogo según el contrato explícito de VEYRA.
 * Excluye inconsistencias contractuales, separa modalidades y lee tarifas con sus condiciones reales.
 */
export const formatCatalogSummary = (catalog) => {
  const providers = catalog?.providers || [];
  const imageModels = [];
  const videoModels = [];

  for (const p of providers) {
    const providerLabel = p.label || p.provider_id || p.id;

    for (const m of p.models || []) {
      const modelLabel = m.label || m.name || m.model_id || m.id;
      const kind = (m.model_kind || m.category || "").toLowerCase();

      // Detección estricta según contrato
      const hasVideoModes = Array.isArray(m.modes) && m.modes.some((x) => x.includes("to_video"));
      const hasImageModes = Array.isArray(m.modes) && m.modes.some((x) => x.includes("to_image"));

      // Inconsistencia contractual: modelo declarado como imagen pero con modos de vídeo
      if (kind === "image" && hasVideoModes && !hasImageModes) {
        // Inconsistencia detectada en contrato de modos; se excluye de recomendaciones
        continue;
      }

      const isVideo = kind === "video" || (hasVideoModes && kind !== "image");
      const isImage = kind === "image" || (hasImageModes && !isVideo) || (m.price_type === "per_image" && !isVideo);

      if (!isVideo && !isImage) continue;

      // Lectura de tarifas con unidad y condiciones reales
      let pricingText = "Tarifas: Pendiente de estimar";
      if (isVideo) {
        if (typeof m.rate_per_second_usd === "number") {
          const isVerified = Boolean(m.price_verified && m.price_source);
          const typeLabel = isVerified ? "Tarifa oficial" : "Estimación interna";
          pricingText = `${typeLabel}: $${m.rate_per_second_usd}/s`;
          if (typeof m.estimated_cost_usd === "number") {
            pricingText += ` (Est. base: $${m.estimated_cost_usd})`;
          }
        } else if (typeof m.estimated_cost_usd === "number") {
          pricingText = `Estimación base: $${m.estimated_cost_usd}`;
        }
      } else {
        // Modelo de imagen
        if (typeof m.price_usd === "number") {
          pricingText = `Tarifa: $${m.price_usd} / imagen fija`;
        } else if (typeof m.cost_per_image === "number") {
          pricingText = `Tarifa: $${m.cost_per_image} / imagen fija`;
        } else if (typeof m.estimated_cost_usd === "number") {
          pricingText = `Estimación base: $${m.estimated_cost_usd} / imagen fija`;
        } else if (m.estimated_cost_usd && typeof m.estimated_cost_usd === "object") {
          const resTiers = Object.entries(m.estimated_cost_usd)
            .map(([res, cost]) => `${res}: $${cost}`)
            .join(", ");
          pricingText = `Tarifas por resolución: [${resTiers}]`;
        } else if (m.price_type === "fixed" && m.rate_per_second_usd) {
          pricingText = `Tarifa: $${m.rate_per_second_usd} / imagen`;
        }
      }

      if (isVideo) {
        let durationText = "Duraciones: según modelo";
        if (Array.isArray(m.durations) && m.durations.length > 0) {
          durationText = `Duraciones admitidas: [${m.durations.join(", ")}s]`;
        } else if (m.min_duration && m.max_duration) {
          durationText = `Duraciones admitidas: ${m.min_duration} a ${m.max_duration}s`;
        }
        const modesText = Array.isArray(m.modes) && m.modes.length > 0 ? `Modos: [${m.modes.join(", ")}]` : "";
        const details = [modesText, durationText, pricingText].filter(Boolean).join(" · ");
        videoModels.push(`  • ${providerLabel} / ${modelLabel}: ${details}`);
      } else {
        const resText = Array.isArray(m.resolutions) && m.resolutions.length > 0 ? `Resoluciones: [${m.resolutions.join(", ")}]` : "";
        const refsText = m.supports_references ? "Soporta referencias" : "Texto-a-imagen";
        const details = [refsText, resText, pricingText].filter(Boolean).join(" · ");
        imageModels.push(`  • ${providerLabel} / ${modelLabel}: ${details}`);
      }
    }
  }

  // Priorizar modelos clave verificados para producción (Wan 3.0, Veo 3.1, Kling 2.6)
  const videoPriority = ["wan 3.0", "veo 3.1", "kling 2.6", "wan 2.7", "seedstory", "flux"];
  videoModels.sort((a, b) => {
    const aLower = a.toLowerCase();
    const bLower = b.toLowerCase();
    const aIdx = videoPriority.findIndex((p) => aLower.includes(p));
    const bIdx = videoPriority.findIndex((p) => bLower.includes(p));
    const aVal = aIdx === -1 ? 999 : aIdx;
    const bVal = bIdx === -1 ? 999 : bIdx;
    return aVal - bVal;
  });

  const imagePriority = ["seedream 5.0", "nano banana 2", "nano banana", "gpt-image", "flux"];
  imageModels.sort((a, b) => {
    const aLower = a.toLowerCase();
    const bLower = b.toLowerCase();
    const aIdx = imagePriority.findIndex((p) => aLower.includes(p));
    const bIdx = imagePriority.findIndex((p) => bLower.includes(p));
    const aVal = aIdx === -1 ? 999 : aIdx;
    const bVal = bIdx === -1 ? 999 : bIdx;
    return aVal - bVal;
  });

  const sections = [];
  sections.push(`Opciones verificadas y pertinentes para el proyecto (criterio: relevancia operativa y compatibilidad técnica con las duraciones y formato del corto; no recomendar por coste sin tarifas comparables):`);
  sections.push(``);
  sections.push(`Modelos verificados para Fotogramas Clave e Imágenes (Image-to-Image / Text-to-Image):`);
  sections.push(imageModels.length > 0 ? imageModels.slice(0, 3).join("\n") : "  (Sin modelos de imagen verificados)");
  sections.push(``);
  sections.push(`Modelos verificados para Generación de Vídeo (Image-to-Video / Text-to-Video):`);
  sections.push(videoModels.length > 0 ? videoModels.slice(0, 3).join("\n") : "  (Sin modelos de vídeo verificados)");

  return sections.join("\n");
};

/**
 * Genera el prompt revisable para Plan de Producción.
 */
export const buildProductionPlanPrompt = ({
  project = {},
  scenes = [],
  characters = [],
  references = [],
  scripts = [],
  activeScript = null,
  assets = [],
  jobs = [],
  catalog = null,
  budget = null,
  proposals = [],
  messages = [],
} = {}) => {
  const projectName = project?.name?.trim() || "Proyecto Sin Título";
  const projectId = project?.id || "";
  const projectFormat = project?.format || "16:9";
  const projectStyle =
    project?.style ||
    project?.documentedStyle ||
    "Estilo cinemático definido por el guion y referencias del proyecto";

  // Recopilar propuestas disponibles (de array proposals o extraídas de messages)
  const allProposals = [...(proposals || []), ...extractProposalsFromMessages(messages)];

  // 1. Guion activo
  const resolvedScript =
    activeScript ||
    (scripts || []).find((sc) => sc.id === project?.activeScriptId || sc.isActive) ||
    null;

  const scriptHeader = resolvedScript
    ? `«${resolvedScript.title}» (Versión: ${resolvedScript.versionId || "v1"})`
    : "Sin guion activo vinculado al proyecto";

  const scriptContentSnippet = resolvedScript
    ? `Guion activo vinculado: «${resolvedScript.title}» (Versión: ${resolvedScript.versionId || "v1"}). Texto y desglose completos transmitidos en el contexto estructurado del proyecto.`
    : "(No se dispone de texto de guion registrado)";

  // 2. Tomas actuales ordenadas
  const sortedScenes = [...(scenes || [])]
    .filter((s) => !s.projectId || s.projectId === projectId)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  // Duraciones
  let totalSavedDuration = 0;
  let totalRecoveredDuration = 0;
  let hasMissingDuration = false;

  const scenesDescriptionList = sortedScenes.map((s, idx) => {
    const num = idx + 1;
    const title = s.title?.trim() || `Toma ${num}`;
    const durResolution = resolveSceneDuration(s, {
      proposals: allProposals,
      activeScript: resolvedScript,
    });

    if (durResolution.isSaved) {
      totalSavedDuration += durResolution.duration;
    } else if (durResolution.isRecovered) {
      totalRecoveredDuration += durResolution.duration;
    } else {
      hasMissingDuration = true;
    }

    const action = s.action?.trim() || "(Sin acción descrita)";
    const camera = s.camera?.trim() || "(Sin cámara especificada)";
    const sound = s.planned_sound?.trim() || "(Sin sonido previsto)";
    const status = s.status || "draft";

    // Referencias visuales asignadas formalmente a esta toma (referenceIds)
    const assignedRefIds = Array.isArray(s.referenceIds) ? s.referenceIds : [];
    const assignedRefLabels = (references || [])
      .filter((r) => assignedRefIds.includes(r.id))
      .map((r) => r.label || r.id);
    const assignedText =
      assignedRefLabels.length > 0
        ? assignedRefLabels.join(", ")
        : "Ninguna referencia visual asignada";

    // Referencias faltantes / pendientes declaradas en la toma (missingReferences)
    const rawMissing = Array.isArray(s.missingReferences) ? s.missingReferences : [];
    const missingAnalysis = rawMissing.map((rawItem) =>
      resolveMissingReferenceStatus(rawItem, {
        characters,
        references,
        scene: s,
      })
    );

    const missingText =
      missingAnalysis.length > 0
        ? missingAnalysis.join("; ")
        : "Ninguna pendiente declarada";

    // Medio actual
    const hasMedia = Boolean(s.selectedAssetId);
    const mediaAsset = hasMedia ? (assets || []).find((a) => a.id === s.selectedAssetId) : null;
    const mediaStatus = mediaAsset
      ? `Medio asignado (${mediaAsset.type || "imagen"}, ID: ${mediaAsset.id})`
      : "Sin medio asignado";

    const promptSnippet = s.prompt?.trim() || "";
    const sceneLines = [
      `[Toma ${num}] ${title} (ID: ${s.id}) — Estado: ${status}`,
      `  • Duración montaje: ${durResolution.details}`,
      `  • Acción: ${action}`,
    ];
    if (promptSnippet) {
      sceneLines.push(`  • Prompt cinematográfico: ${promptSnippet}`);
    }
    sceneLines.push(
      `  • Cámara: ${camera}`,
      `  • Sonido previsto: ${sound}`,
      `  • Estado medio: ${mediaStatus}`,
      `  • Referencias visuales asignadas: ${assignedText}`,
      `  • Referencias pendientes analizadas: ${missingText}`,
    );

    return sceneLines.join("\n");
  });

  const scenesDescription =
    scenesDescriptionList.length > 0
      ? scenesDescriptionList.join("\n\n")
      : "(No hay tomas registradas en el storyboard para este proyecto)";

  // Encabezado de duración total sin sumas parciales engañosas
  let totalDurationHeader = "";
  if (totalRecoveredDuration > 0) {
    const totalComplete = totalSavedDuration + totalRecoveredDuration;
    totalDurationHeader = `${totalComplete}s (${totalSavedDuration}s consolidados en base de datos + ${totalRecoveredDuration}s procedentes de la propuesta aceptada v1 para la apertura; pendiente de consolidación física)`;
  } else if (hasMissingDuration) {
    totalDurationHeader = `Suma parcial guardada: ${totalSavedDuration}s (Incompleta: existen tomas sin duración guardada)`;
  } else {
    totalDurationHeader = `${totalSavedDuration}s`;
  }

  // 3. Inventario de recursos registrados en el proyecto
  const isDemoProj = projectId === "prj_demo01";
  const registeredChars = (characters || []).filter((c) => {
    if (!isDemoProj && (c.isDemo || ["personajea", "botella", "azotea"].includes((c.handle || "").replace(/^@+/, "").toLowerCase()))) return false;
    return !c.projectId || c.projectId === projectId;
  });
  const charsList =
    registeredChars.length > 0
      ? registeredChars
          .map((c) => {
            const h = (c.handle || "").replace(/^@+/, "");
            const k = c.kind ? ` [${c.kind}]` : "";
            return c.name ? `${c.name} (@${h})${k}` : `@${h}${k}`;
          })
          .join("; ")
      : "Sin personajes ni objetos registrados en la biblioteca del proyecto";

  // 4. Referencias visuales registradas en el proyecto
  const projectRefs = (references || []).filter((r) => {
    if (!isDemoProj && (r.isDemo || ["ref_demo01", "ref_demo02", "ref_demo03"].includes(r.id) || ["@personajea", "@botella", "@azotea"].includes((r.label || "").toLowerCase()))) return false;
    return !r.projectId || r.projectId === projectId;
  });
  const refsList =
    projectRefs.length > 0
      ? projectRefs
          .map((r) => {
            const hasImg = Boolean(r.assetId || (Array.isArray(r.views) && r.views.some((v) => v.assetId)));
            const imgStatus = hasImg ? "Con imagen válida" : "Sin imagen asignada";
            return `${r.label || r.id} (ID: ${r.id}, ${imgStatus})`;
          })
          .join("; ")
      : "Sin referencias visuales registradas";

  // 5. Medios y tareas existentes
  const projectAssets = (assets || []).filter((a) => !a.projectId || a.projectId === projectId);
  const projectJobs = (jobs || []).filter((j) => !j.projectId || j.projectId === projectId);

  // 6. Catálogo de modelos pertinentes y tarifas
  const catalogSummary = formatCatalogSummary(catalog);

  // 7. Presupuesto
  const budgetInfo = budget?.monthlyLimit
    ? `Límite mensual del estudio: $${budget.monthlyLimit} USD (Directiva de control de gastos del estudio; no es saldo precargado ni presupuesto exclusivo asignado a este corto)`
    : "Sin límite mensual configurado en el estudio";

  const directives = [
    `=== DIRECTIVAS OBLIGATORIAS PARA EL PLAN DE PRODUCCIÓN ===`,
    `Presenta un plan claro, profesional y estructurado en 7 secciones:`,
    `1. QUÉ ESTÁ LISTO Y QUÉ FALTA PREPARAR: Tomas y medios ya aprobados para NO regenerarlos innecesariamente. Elementos faltantes antes de entrar a generación de vídeo.`,
    `2. CONTRASTE Y GESTIÓN DE REFERENCIAS: Distingue con total claridad entre: recurso en biblioteca, ref con imagen válida, ref asignada a toma (referenceIds) y ref pendiente declarada por el guion. Si ya cuenta con imagen válida en biblioteca, NO solicitar conceptualizarlo ni generarlo de nuevo; indicar pendiente de asignación formal a la toma. Si es candidato por coincidencia no inequívoca, indicarlo como candidato por confirmar. Señalar tomas para mantener raccord.`,
    `3. ORDEN RECOMENDADO DE TRABAJO (HOJA DE RUTA): Paso 1: Generación y aprobación de referencias visuales sin imagen maestra. Paso 2: Generación de imágenes base (Keyframes) por plano antes de generar vídeo. Paso 3: Generación de vídeo (Image-to-Video) a partir de los fotogramas base aprobados. Paso 4: Diseño sonoro y foley. Paso 5: Montaje y ensamblaje final.`,
    `4. DEPENDENCIAS CONCRETAS ENTRE PLANOS: Especifica qué planos dependen visualmente de los anteriores para raccord y reutilización de fotogramas aprobados.`,
    `5. MODELOS COMPATIBLES, DURACIONES FACTURABLES Y COSTES: Distingue duración de montaje (narrativa) de duración facturable (soportada por cada modelo según catálogo). Si no constan tarifas verificables en catálogo para algún modelo, indicar estrictamente «Pendiente de estimar» sin inventar precios.`,
    `6. RESOLUCIÓN DE ELEMENTOS NARRATIVOS CLAVE: Clic del control: Efecto de sonido foley sincronizado exactamente con la pulsación en montaje. Persiana que baja: Transición visual Image-to-Video desde fotograma con persiana abierta hacia persiana cerrada. Letrero «Cerrado por drama»: Fotograma clave de imagen estática sostenido durante la duración de montaje en edición. No afirmes que herramientas inexistentes están disponibles ni que los medios ya fueron generados si no constan en los activos reales.`,
    `7. NATURALEZA CONSULTIVA: Este plan es una guía estratégica y consultiva para el realizador; no crea escenas, no asigna recursos ni envía generaciones automáticamente.`,
    `Para que la interfaz de VEYRA reconozca tu plan y permita al realizador revisarlo en ventana independiente y activarlo, emite la respuesta incluyendo un bloque delimitado <veyra_plan> con un JSON estructurado (id, title, version, projectId, summary, content, sections).`,
  ].join("\n\n");

  return [
    `Actúa como Creative Director de VEYRA y prepara un PLAN DE PRODUCCIÓN cinematográfico y estructurado para este proyecto:`,
    ``,
    `=== 1. CONTEXTO GENERAL DEL PROYECTO ===`,
    `• Proyecto: ${projectName} (ID: ${projectId})`,
    `• Guion activo: ${scriptHeader}`,
    `• Formato de pantalla: ${projectFormat}`,
    `• Estilo documentado: ${projectStyle}`,
    `• Presupuesto del estudio: ${budgetInfo}`,
    ``,
    `=== GUION ACTIVO ===`,
    scriptContentSnippet,
    ``,
    `=== 2. TOMAS ACTUALES DEL STORYBOARD (${sortedScenes.length} tomas · Duración total de montaje: ${totalDurationHeader}) ===`,
    `Nota: El medio asignado es un fotograma preliminar/borrador; no equivale a medio final aprobado, toma animada ni tarea completada.`,
    ``,
    scenesDescription,
    ``,
    `=== 3. INVENTARIO REAL DE RECURSOS, REFERENCIAS Y MEDIOS ===`,
    `• Recursos registrados en el proyecto: ${charsList}`,
    `• Referencias visuales registradas en el proyecto: ${refsList}`,
    `• Medios existentes en el proyecto: ${projectAssets.length} activos`,
    `• Tareas/jobs registrados: ${projectJobs.length} tareas`,
    ``,
    `=== 4. CATÁLOGO DE MODELOS PERTINENTES Y TARIFAS ===`,
    catalogSummary,
    ``,
    directives,
  ].join("\n");
};

/**
 * Detecta si el texto del usuario es una solicitud de corrección, ajuste o actualización de un Plan de Producción.
 */
export const isPlanCorrectionRequest = (text) => {
  if (!text || typeof text !== "string") return false;
  const lower = text.toLowerCase();
  return (
    /corrige.*(plan|planificaci[oó]n)/i.test(lower) ||
    /ajusta.*(plan|planificaci[oó]n)/i.test(lower) ||
    /modifica.*(plan|planificaci[oó]n)/i.test(lower) ||
    /revisa.*(plan|planificaci[oó]n)/i.test(lower) ||
    /actualiza.*(plan|planificaci[oó]n)/i.test(lower) ||
    /solicitud de ajustes sobre el plan/i.test(lower) ||
    /<veyra_plan>/i.test(lower) ||
    /(nueva versi[oó]n|nuevo borrador).*(plan|planificaci[oó]n)/i.test(lower)
  );
};

/**
 * Localiza el plan más reciente emitido en los mensajes del hilo (sin seleccionarlo automáticamente como activo).
 */
export const findLatestPlanInMessages = (messages = [], projectId = null) => {
  if (!Array.isArray(messages)) return null;
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i];
    if (m?.role === "assistant" && typeof m.content === "string") {
      const plans = extractPlansFromContent(m.content, { projectId, messageId: m.id });
      if (plans.length > 0) {
        return plans[plans.length - 1];
      }
    }
  }
  return null;
};

/**
 * Valida un plan estructurado contra el contrato de la interfaz y las duraciones del Storyboard.
 * Señala discrepancias sin reparar silenciosamente decisiones del contenido.
 */
export const validatePlanAgainstStoryboard = (
  plan,
  { storyboardScenes = [], allProposals = [], activeScript = null } = {}
) => {
  if (!plan) return plan;

  const contractErrors = [];

  // 1. Validación de contrato de campos de identificación obligatorios
  const rawJson = plan.rawJson;
  if (rawJson && typeof rawJson === "object") {
    if (!rawJson.title && (!plan.title || plan.title === "Plan de Producción")) {
      contractErrors.push("Falta el campo obligatorio de identificación 'title'");
    }
    if (!rawJson.version && (!plan.version || plan.version === "v1")) {
      contractErrors.push("Falta el campo obligatorio de identificación 'version'");
    }
    if (!rawJson.summary && !plan.summary) {
      contractErrors.push("Falta el campo obligatorio de identificación 'summary'");
    }
    if (rawJson.sections && !Array.isArray(rawJson.sections)) {
      contractErrors.push("El campo 'sections' debe ser una lista (Array) de secciones estructuradas, no un objeto plano");
    }
  }

  // 2. Validación de duraciones contra el Storyboard
  const sortedScenes = [...(storyboardScenes || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  const storyboardDurations = {};
  sortedScenes.forEach((s, idx) => {
    const order = idx + 1;
    const durRes = resolveSceneDuration(s, { proposals: allProposals, activeScript });
    if (durRes && typeof durRes.duration === "number") {
      storyboardDurations[order] = durRes.duration;
    }
  });

  // Extraer duraciones declaradas en el plan
  const planDurations = {};

  // a. Desde rawJson.sections.perTakeResolution o rawJson.perTakeResolution
  const perTake =
    (Array.isArray(rawJson?.sections?.perTakeResolution) && rawJson.sections.perTakeResolution) ||
    (Array.isArray(rawJson?.perTakeResolution) && rawJson.perTakeResolution) ||
    (Array.isArray(rawJson?.takes) && rawJson.takes) ||
    [];

  for (const item of perTake) {
    const ord = Number(item.order || item.take || item.sceneNumber);
    const dur = Number(item.durationSeconds || item.duration || item.duration_seconds);
    if (!isNaN(ord) && !isNaN(dur) && dur > 0) {
      planDurations[ord] = dur;
    }
  }

  // b. Desde tabla markdown en content si no constaba en JSON: | 1 | 3 s |
  const contentText = plan.content || "";
  if (Object.keys(planDurations).length === 0 && contentText) {
    const tableMatches = [...contentText.matchAll(/\|\s*(\d+)\s*\|\s*(\d+)\s*s?\s*\|/g)];
    for (const m of tableMatches) {
      const ord = Number(m[1]);
      const dur = Number(m[2]);
      if (!isNaN(ord) && !isNaN(dur) && dur > 0) {
        planDurations[ord] = dur;
      }
    }
  }

  // Comparar duraciones
  const discrepancies = [];
  for (const [orderStr, sbDur] of Object.entries(storyboardDurations)) {
    const order = Number(orderStr);
    const planDur = planDurations[order];
    if (typeof planDur === "number" && planDur !== sbDur) {
      discrepancies.push({
        order,
        storyboardDuration: sbDur,
        planDuration: planDur,
      });
    }
  }

  plan.contractErrors = contractErrors;
  plan.hasContractError = contractErrors.length > 0;
  plan.isContractValid = contractErrors.length === 0;

  plan.durationDiscrepancies = discrepancies;
  plan.hasDurationDiscrepancy = discrepancies.length > 0;

  return plan;
};

/**
 * Genera el prompt enriquecido para continuación o corrección de un Plan de Producción.
 * Reutiliza el catálogo existente y duraciones del Storyboard manteniéndose bajo el límite de 15.000 caracteres.
 */
export const buildPlanCorrectionPrompt = ({
  userMessage = "",
  plan = null,
  project = {},
  scenes = [],
  characters = [],
  references = [],
  scripts = [],
  activeScript = null,
  assets = [],
  jobs = [],
  catalog = null,
  budget = null,
  messages = [],
  proposals = [],
} = {}) => {
  const projectName = project?.name?.trim() || "Proyecto Sin Título";
  const projectId = project?.id || "";
  const currentCanonical = extractCanonicalPlanVersion(plan, plan?.version || "v1");
  const currNum = parseInt(currentCanonical.replace(/\D/g, ""), 10) || 1;
  const nextVersion = `v${currNum + 1}`;
  const baseTitle = cleanPlanTitle(plan?.title);
  const prevPlanId = plan?.id || "";

  // Recopilar propuestas
  const allProposals = [...(proposals || []), ...extractProposalsFromMessages(messages)];

  const resolvedScript =
    activeScript ||
    (scripts || []).find((sc) => sc.id === project?.activeScriptId || sc.isActive) ||
    null;

  const scriptHeader = resolvedScript
    ? `«${resolvedScript.title}» (Versión: ${resolvedScript.versionId || "v1"})`
    : "Sin guion activo";

  // Tomas actuales ordenadas
  const sortedScenes = [...(scenes || [])]
    .filter((s) => !s.projectId || s.projectId === projectId)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  let totalSavedDuration = 0;
  let totalRecoveredDuration = 0;
  const scenesSummaryLines = sortedScenes.map((s, idx) => {
    const num = idx + 1;
    const durRes = resolveSceneDuration(s, {
      proposals: allProposals,
      activeScript: resolvedScript,
    });
    if (durRes.isSaved) totalSavedDuration += durRes.duration;
    else if (durRes.isRecovered) totalRecoveredDuration += durRes.duration;

    const assignedRefIds = Array.isArray(s.referenceIds) ? s.referenceIds : [];
    const assignedRefLabels = (references || [])
      .filter((r) => assignedRefIds.includes(r.id))
      .map((r) => r.label || r.id);
    const assignedText = assignedRefLabels.length > 0 ? assignedRefLabels.join(", ") : "Ninguna";

    const mediaAsset = s.selectedAssetId ? (assets || []).find((a) => a.id === s.selectedAssetId) : null;
    const mediaText = mediaAsset ? `Medio asignado (${mediaAsset.type || "imagen"})` : "Sin medio";

    return `[Toma ${num}] ${s.title || `Toma ${num}`} (${durRes.details}): ${s.action || s.prompt || "(Sin acción)"} | Cámara: ${s.camera || "No especificada"} | Refs: ${assignedText} | Estado: ${mediaText}`;
  });

  const totalRuntime = totalSavedDuration + totalRecoveredDuration;
  const totalRuntimeText = totalRecoveredDuration > 0
    ? `${totalRuntime}s (${totalSavedDuration}s consolidados + ${totalRecoveredDuration}s apertura propuesta v1)`
    : `${totalRuntime}s`;

  // Resumen del catálogo
  const catalogSummary = formatCatalogSummary(catalog);

  // Resumen del plan previo bajo revisión
  const prevPlanSnippet = plan?.summary ||
    (typeof plan?.content === "string" ? plan.content.slice(0, 400).replace(/\n+/g, " ") + "..." : "Plan previo disponible para ajuste");

  const promptParts = [
    `Solicitud de corrección y nueva versión (${nextVersion}) sobre el Plan de Producción «${baseTitle}» (${currentCanonical}):`,
    ``,
    `=== INSTRUCCIONES DEL REALIZADOR PARA ESTA CORRECCIÓN ===`,
    userMessage.trim(),
    ``,
    `=== ESTADO Y PLAN PREVIO OBJETO DE REVISIÓN ===`,
    `• Proyecto: ${projectName} (${projectId}) | Guion: ${scriptHeader} | Duración total Storyboard: ${totalRuntimeText}`,
    `• Plan previo: «${baseTitle}» (${currentCanonical}, ID previo: ${prevPlanId})`,
    `• Resumen previo: ${prevPlanSnippet}`,
    ``,
    `=== TOMAS Y DURACIONES REALES DEL STORYBOARD (${sortedScenes.length} tomas · Total: ${totalRuntimeText}) ===`,
    `Nota: Respeta fielmente las duraciones individuales preparadas por toma; no las unifiques a 3 segundos si difieren.`,
    scenesSummaryLines.join("\n"),
    ``,
    `=== CATÁLOGO DE MODELOS DISPONIBLES Y TARIFAS DEL ESTUDIO ===`,
    catalogSummary,
    ``,
    `=== REGLAS OBLIGATORIAS Y CONTRATO DEL PLAN ===`,
    `1. SEPARACIÓN DE MODELOS: El modelo conversacional (ej. deepseek-chat) es ÚNICAMENTE el asistente de diálogo. NUNCA lo indiques como el modelo de vídeo ni de imagen. Para vídeo e imagen selecciona exclusivamente opciones del catálogo (ej. Kling, Hailuo, Luma, Wan, Flux, etc.). Si falta definir modelo o tarifa, indica estrictamente «Pendiente de estimar».`,
    `2. FIDELIDAD AL STORYBOARD: Conserva las duraciones individuales reales del Storyboard (${sortedScenes.map((s, idx) => `T${idx + 1}:${resolveSceneDuration(s, { proposals: allProposals, activeScript: resolvedScript }).duration}s`).join(", ")}). No afirmes que herramientas inexistentes están disponibles ni generes medios automáticamente.`,
    `3. CONTRATO <veyra_plan>: Emite la respuesta incluyendo un bloque delimitado <veyra_plan> con un JSON estructurado según el contrato de VEYRA:`,
    `{`,
    `  "id": "plan_${uid("plan")}",`,
    `  "title": "${baseTitle}",`,
    `  "version": "${nextVersion}",`,
    `  "previousPlanId": "${prevPlanId}",`,
    `  "projectId": "${projectId}",`,
    `  "summary": "Resumen ejecutivo del plan corregido...",`,
    `  "content": "Contenido completo estructurado en Markdown...",`,
    `  "sections": [`,
    `    { "id": "s1", "title": "Qué está listo y qué falta preparar", "content": "..." },`,
    `    { "id": "s2", "title": "Resolución por toma y partidas", "content": "..." }`,
    `  ]`,
    `}`,
    `REGLA DE IDENTIDAD Y VERSIÓN: La nueva versión estructurada asignada desde la aplicación es estrictamente «${nextVersion}». Conserva el título base «${baseTitle}» sin acumular sufijos de versiones ni cadenas como «(vX, corregido)». El vínculo con la revisión previa se conserva en 'previousPlanId': "${prevPlanId}".`,
    `IMPORTANTE: El campo 'sections' DEBE ser una lista/array de objetos con id, title y content (no un objeto plano).`,
  ];

  return promptParts.join("\n");
};

/**
 * Extrae planes de producción de una respuesta del Asistente/Director.
 */
export const extractPlansFromContent = (
  content,
  { projectId = null, messageId = null, scenes = [], proposals = [], activeScript = null } = {}
) => {
  if (!content || typeof content !== "string") return [];

  const plans = [];

  // Texto fuera del bloque <veyra_plan> para preservar el texto legible completo si el JSON no tiene 'content'
  const messageTextWithoutPlan = content.replace(/<veyra_plan>[\s\S]*?<\/veyra_plan>/g, "").trim();
  const headingMatch = content.match(/^#\s+(.+)$/m);
  const versionMatch = (headingMatch ? headingMatch[1] : "").match(/\((v\d+)[^)]*\)/i) || content.match(/\b(v[1-9]\d*)\b/i);

  // 1. Detección por etiquetas <veyra_plan>...</veyra_plan>
  const matches = [...content.matchAll(/<veyra_plan>\s*([\s\S]*?)\s*<\/veyra_plan>/g)];
  for (const match of matches) {
    const raw = match[1].trim();
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        const rawContent = parsed.content;
        const resolvedContent = (typeof rawContent === "string" && rawContent.length > 50)
          ? rawContent
          : (messageTextWithoutPlan.length > 50 ? messageTextWithoutPlan : raw);

        const planObj = {
          id: parsed.id || uid("plan"),
          projectId: parsed.projectId || projectId,
          title: parsed.title || (headingMatch ? headingMatch[1].trim() : "Plan de Producción"),
          version: parsed.version || (versionMatch ? versionMatch[1] : "v1"),
          previousPlanId: parsed.previousPlanId || parsed.previous_plan_id || null,
          summary: parsed.summary || (messageTextWithoutPlan ? messageTextWithoutPlan.slice(0, 180).replace(/\n+/g, " ") + "..." : "Plan de producción preparado"),
          content: resolvedContent,
          sections: Array.isArray(parsed.sections) ? parsed.sections : [],
          messageId: parsed.messageId || messageId,
          rawJson: parsed,
        };

        validatePlanAgainstStoryboard(planObj, {
          storyboardScenes: scenes,
          allProposals: proposals,
          activeScript,
        });

        plans.push(planObj);
        continue;
      }
    } catch {
      // Si no es JSON pero está entre las etiquetas, es un plan en markdown
      const planObj = {
        id: uid("plan"),
        projectId,
        title: headingMatch ? headingMatch[1].trim() : "Plan de Producción",
        version: versionMatch ? versionMatch[1] : "v1",
        summary: raw.slice(0, 200).replace(/\n+/g, " ") + "...",
        content: messageTextWithoutPlan.length > 50 ? messageTextWithoutPlan : raw,
        sections: [],
        messageId,
      };

      validatePlanAgainstStoryboard(planObj, {
        storyboardScenes: scenes,
        allProposals: proposals,
        activeScript,
      });

      plans.push(planObj);
    }
  }

  // 2. Detección heurística si no hay etiqueta pero el mensaje tiene estructura explícita de Plan de Producción
  if (plans.length === 0) {
    const isExplicitPlan =
      (content.includes("# PLAN DE PRODUCCIÓN") ||
        content.includes("### PLAN DE PRODUCCIÓN") ||
        content.includes("Plan de Producción") ||
        content.includes("PLAN DE PRODUCCIÓN")) &&
      content.includes("ORDEN RECOMENDADO") &&
      content.includes("DEPENDENCIAS");

    if (isExplicitPlan) {
      const planObj = {
        id: uid("plan"),
        projectId,
        title: headingMatch ? headingMatch[1].trim() : "Plan de Producción",
        version: versionMatch ? versionMatch[1] : "v1",
        summary: content.slice(0, 200).replace(/\n+/g, " ") + "...",
        content: content.trim(),
        sections: [],
        messageId,
      };

      validatePlanAgainstStoryboard(planObj, {
        storyboardScenes: scenes,
        allProposals: proposals,
        activeScript,
      });

      plans.push(planObj);
    }
  }

  return plans;
};

/**
 * Calcula una firma o snapshot del estado del proyecto para verificar vigencia.
 */
export const computePlanSnapshot = ({
  project = {},
  scenes = [],
  characters = [],
  references = [],
  activeScript = null,
} = {}) => {
  const projectId = project?.id || "";
  const sortedScenes = [...(scenes || [])]
    .filter((s) => !s.projectId || s.projectId === projectId)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const scenesFingerprint = sortedScenes.map((s) => ({
    id: s.id,
    order: s.order,
    duration: typeof s.duration === "number" ? s.duration : null,
    promptLength: (s.prompt || "").trim().length,
    refIds: Array.isArray(s.referenceIds) ? [...s.referenceIds].sort().join(",") : "",
    assetId: s.selectedAssetId || null,
  }));

  const charsCount = (characters || []).filter(
    (c) => !c.projectId || c.projectId === projectId
  ).length;

  const refsCount = (references || []).filter(
    (r) => !r.projectId || r.projectId === projectId
  ).length;

  return {
    projectId,
    scriptId: activeScript?.id || project?.activeScriptId || null,
    scriptVersionId: activeScript?.versionId || null,
    scenesCount: sortedScenes.length,
    scenes: scenesFingerprint,
    charsCount,
    refsCount,
    computedAt: new Date().toISOString(),
  };
};

/**
 * Comprueba la vigencia de un plan activo respecto al estado actual del proyecto.
 * Retorna { isOutdated: boolean, reasons: string[] }.
 */
export const checkPlanValidity = (
  activePlan,
  {
    project = {},
    scenes = [],
    characters = [],
    references = [],
    activeScript = null,
  } = {}
) => {
  if (!activePlan || !activePlan.snapshot) {
    return { isOutdated: false, reasons: [] };
  }

  const snap = activePlan.snapshot;
  const currentSnap = computePlanSnapshot({
    project,
    scenes,
    characters,
    references,
    activeScript,
  });

  const reasons = [];

  // Comprobar guion
  if (snap.scriptId && currentSnap.scriptId && snap.scriptId !== currentSnap.scriptId) {
    reasons.push("Se cambió el guion activo del proyecto.");
  } else if (
    snap.scriptVersionId &&
    currentSnap.scriptVersionId &&
    snap.scriptVersionId !== currentSnap.scriptVersionId
  ) {
    reasons.push(`El guion avanzó de versión (de ${snap.scriptVersionId} a ${currentSnap.scriptVersionId}).`);
  }

  // Comprobar número de tomas
  if (snap.scenesCount !== currentSnap.scenesCount) {
    reasons.push(
      `El número de tomas del storyboard varió (antes ${snap.scenesCount}, ahora ${currentSnap.scenesCount}).`
    );
  } else {
    // Comprobar diferencias por toma
    for (let i = 0; i < snap.scenes.length; i++) {
      const oldScn = snap.scenes[i];
      const newScn = currentSnap.scenes[i];
      if (!newScn) continue;

      if (oldScn.id !== newScn.id) {
        reasons.push(`El orden o identidad de la Toma ${i + 1} fue modificado.`);
        break;
      }
      if (oldScn.duration !== newScn.duration) {
        reasons.push(
          `La duración de la Toma ${i + 1} cambió de ${oldScn.duration ?? "no definida"}s a ${newScn.duration ?? "no definida"}s.`
        );
      }
      if (oldScn.refIds !== newScn.refIds) {
        reasons.push(`Se modificaron las referencias asignadas a la Toma ${i + 1}.`);
      }
    }
  }

  return {
    isOutdated: reasons.length > 0,
    reasons,
  };
};
